export const fieldUniqueIdentifier = field => `${field.fieldName}-${field.nameOfModel}`;

export function setupModifiedFieldsState(companyOverrides) {
  return companyOverrides.reduce((modifiedFieldsByIdentifier, fieldOverride) => {
    modifiedFieldsByIdentifier[fieldUniqueIdentifier(fieldOverride)] = fieldOverride;
    return modifiedFieldsByIdentifier;
  }, {});
}

export const SECTION_ORDER = [
  'database',
  'lease settings',
  'sale settings',
  'location',
  'property information',
  'building information',
  'units',
  'land information',
  'sale information',
  'lots',
  'condos',
  'financials',
  'lease information',
  'lease spaces'
];

export const INFO_TEXT = {
  'Research::Property': {
    'lot_size': 'This is always required for land property types',
    'number_of_units': 'this is always required for a multifamily property',
    'building_class': 'This is always required for office property types'
  },
  'LeaseSpace': {
    'address2': 'This is never required for a property that has a single tenant'
  }
};

const LABEL_OVERRIDE = {
  'Property': {
    'name': 'Property Name',
    'lease_commission_pct': 'Commission Split %',
    'commission_pct': 'Commission %'
  },
  'Research::Property': {
    'occupancy_pct': 'Occupancy %',
    'grade_level_doors': 'Number of Grade Level Doors',
    'dock_high_doors': 'Number of Dock High Doors',
    'drive_in_bays': 'Number of Drive in Bays',
    'ceiling_height_min': 'Minimum Ceiling Height',
    'industrial_office_space': 'Office Space',
    'stories': 'Number of Floors',
    'typical_floor_size': 'Average Floor Size',
    'renovated': 'Year Last Renovated',
    'apn': 'APN#'
  },
  'LeaseListing': {
    'title': 'Lease Title',
    'description': 'Lease Description'
  },
  'LeaseSpace': {
    'address2': 'Space Name (Address 2)',
    'zip4': 'Zip+4',
    'min_divisible_area': 'Min Divisible',
    'max_contiguous_area': 'Max Contiguous',
    'lease_term': 'Lease Term (Months)'
  },
  'Projects::LeaseAgreement': {
    'bullets': 'Lease Bullets',
    'expiration': 'Listing Expiration - Lease'
  },
  'FinancialStatement': {
    'net_operating_income_override': 'NOI',
    'cap_rate_override': 'Cap Rate'
  },
  'SaleListing': {
    'title': 'Sale Title',
    'description': 'Sale Description',
    'nnn_years_left_on_lease': 'Years Left on Lease',
    'nnn_lease_expiration': 'NNN Lease Expiration',
    'buyer_lead_id': 'Buyer',
    'buyer_referral_source_id': 'Referral Source'
  },
  'LotListing': {
    'buyer_lead_id': 'Buyer',
    'buyer_referral_source_id': 'Referral Source'
  },
  'Projects::SaleAgreement': {
    'expiration': 'Listing Expiration - Sale',
    'bullets': 'Sale Bullets'
  },
  'PropertyTypeMapping': {
    'property_type_id': 'Primary Property Type'
  }
};

const FIELD_TYPE_OVERRIDE = {
  'SaleListing': {
    'nnn_years_left_on_lease': 'Dropdown'
  },
  'PropertyTypeMapping': {
    'property_type_id': 'Dropdown'
  },
  'Research::Property': {
    'investment_type': 'Options'
  }
};

const SECTION_FOR_FIELD = {
  'SaleListing': {
    'close_date': 'sale settings',
    'buyer_lead_id': 'sale settings',
    'buyer_referral_source_id': 'sale settings',
    'title': 'sale information',
    'description': 'sale information',
    'nnn_years_left_on_lease': 'sale information',
    'nnn_lease_expiration': 'sale information'
  },
  'Property': {
    'database': 'database',
    'sale_listed_on': 'sale settings',
    'lease_listed_on': 'lease settings',
    'lease_closing_info': 'lease information',
    'lease_commission_pct': 'lease information',
    'commission_pct': 'sale information',
    'sale_closing_info': 'sale information',
    'name': 'property information',
    'location_description': 'location'
  },
  'Projects::SaleAgreement': {
    'expiration': 'sale settings',
    'bullets': 'sale information'
  },
  'Projects::LeaseAgreement': {
    'expiration': 'lease settings',
    'bullets': 'lease information'
  },
  'LeaseListing': {
    'title': 'lease information',
    'description': 'lease information'
  },
  'Research::Property': {
    'property_use_id': 'sale information',
    'investment_type': 'sale information',
    'zoning': 'property information',
    'lot_size': 'property information',
    'apn': 'property information',
    'address': 'location',
    'zip': 'location',
    'city': 'location',
    'state': 'location',
    'county': 'location',
    'market': 'location',
    'submarket': 'location',
    'cross_streets': 'location',
    'number_of_units': 'units',
    'building_size': 'building information',
    'building_class': 'building information',
    'occupancy_pct': 'building information',
    'tenancy_id': 'building information',
    'grade_level_doors': 'building information',
    'dock_high_doors': 'building information',
    'drive_in_bays': 'building information',
    'year_built': 'building information',
    'number_of_cranes': 'building information',
    'dock_door_description': 'building information',
    'crane_description': 'building information',
    'ceiling_height': 'building information',
    'sprinkler_description': 'building information',
    'ceiling_height_min': 'building information',
    'industrial_office_space': 'building information',
    'stories': 'building information',
    'typical_floor_size': 'building information',
    'renovated': 'building information',
    'number_of_lots': 'land information',
    'best_use': 'land information'
  },
  'PropertyTypeMapping': {
    'property_type_id': 'property information'
  },
  'CondoListing': {
    'close_date': 'condos'
  },
  'Condo': {
    'address2': 'condos',
    'sale_price': 'condos',
    'size': 'condos',
    'description': 'condos'
  },
  'Lot': {
    'address': 'lots',
    'sale_price': 'lots',
    'description': 'lots',
    'zoning': 'lots',
    'number': 'lots',
    'property_subtype_id': 'lots',
    'apn': 'lots',
    'size': 'lots'
  },
  'LotListing': {
    'close_date': 'lots',
    'buyer_lead_id': 'lots',
    'buyer_referral_source_id': 'lots'
  },
  'LeaseSpace': {
    'space_type_id': 'lease spaces',
    'tenant_name': 'lease spaces',
    'suite': 'lease spaces',
    'floor': 'lease spaces',
    'lease_rate_units': 'lease spaces',
    'date_available': 'lease spaces',
    'ceiling_height': 'lease spaces',
    'description': 'lease spaces',
    'min_divisible_area': 'lease spaces',
    'max_contiguous_area': 'lease spaces',
    'address2': 'lease spaces',
    'zip4': 'lease spaces',
    'lease_rate': 'lease spaces',
    'space_size': 'lease spaces',
    'lease_type_id': 'lease spaces',
    'lease_term': 'lease spaces'
  },
  'LeaseSpaceListing': {
    'close_date': 'lease spaces'
  },
  'FinancialStatement': {
    'name': 'financials',
    'sale_price': 'financials',
    'net_operating_income_override': 'financials',
    'cap_rate_override': 'financials'
  }
};

export function modifyBaseFields(baseFields) {
  return baseFields.map(field => (
    {
      ...field,
      label: LABEL_OVERRIDE[field.nameOfModel]?.[field.fieldName] || field.label,
      fieldType: FIELD_TYPE_OVERRIDE[field.nameOfModel]?.[field.fieldName] || field.fieldType,
      section: SECTION_FOR_FIELD[field.nameOfModel][field.fieldName]
    }
  ));
}
